// src/pages/admin/AdminPendingUsersPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../Api/apiConfig";
import { CheckCircle, Mail, Loader, XCircle, Trash2 } from "lucide-react";
import "../../styles/AdminPage.css";

const AdminPendingUsersPage = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({}); // success, mail_sent, deleted, loading, error

  // ----------- Fetch des pending users -----------
  const fetchPendingUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get("/users/admin/pending-users");
      setPendingUsers(Array.isArray(response.data) ? response.data : []);
      setActionStatus({});
    } catch (err) {
      console.error("Erreur récupération liste :", err);
      setError(
        err.response?.data?.error ||
          "Impossible de charger les utilisateurs en attente."
      );
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  const clearStatus = (userId, delay = 3000) => {
    setTimeout(() => {
      setActionStatus((prev) => ({ ...prev, [userId]: undefined }));
    }, delay);
  };

  // ----------- VALIDATION MANUELLE -----------
  const handleValidateManual = async (id, email) => {
    if (!window.confirm(`Valider manuellement le compte de ${email} ?`)) return;

    try {
      setActionStatus((prev) => ({ ...prev, [id]: "loading" }));

      await apiClient.post("/users/admin/validate-manual", { id });

      setPendingUsers((prev) => prev.filter((u) => u.id !== id));
      setActionStatus((prev) => ({ ...prev, [id]: "success" }));

      alert(`Utilisateur ${email} validé.`);
    } catch (err) {
      console.error("Erreur validation :", err);
      setActionStatus((prev) => ({ ...prev, [id]: "error" }));
      alert(
        err.response?.data?.error || "Erreur lors de la validation manuelle."
      );
      clearStatus(id);
    }
  };

  // ----------- RÉENVOI EMAIL VALIDATION -----------
  const handleResendEmail = async (id, email) => {
    if (!window.confirm(`Renvoyer l'email à ${email} ?`)) return;

    try {
      setActionStatus((prev) => ({ ...prev, [id]: "loading" }));

      await apiClient.post("/users/admin/resend-email", { id });

      setActionStatus((prev) => ({ ...prev, [id]: "mail_sent" }));
      alert(`Email envoyé à ${email}.`);

      clearStatus(id);
    } catch (err) {
      console.error("Erreur envoi email :", err);
      setActionStatus((prev) => ({ ...prev, [id]: "error" }));
      alert(err.response?.data?.error || "Erreur lors de l’envoi de l’email.");
      clearStatus(id);
    }
  };

  // ----------- SUPPRESSION PENDING USER -----------
  const handleDeletePendingUser = async (id, email) => {
    if (
      !window.confirm(
        `Supprimer définitivement l'utilisateur en attente ${email} ?`
      )
    )
      return;

    try {
      setActionStatus((prev) => ({ ...prev, [id]: "loading" }));

      await apiClient.delete(`/users/admin/pending-users/${id}`);

      setPendingUsers((prev) => prev.filter((u) => u.id !== id));
      setActionStatus((prev) => ({ ...prev, [id]: "deleted" }));
      alert(`Utilisateur ${email} supprimé.`);
    } catch (err) {
      console.error("Erreur suppression :", err);
      setActionStatus((prev) => ({ ...prev, [id]: "error" }));
      alert(err.response?.data?.error || "Erreur lors de la suppression.");
      clearStatus(id);
    }
  };

  // ----------- RENDU -----------
  if (loading)
    return (
      <div className="admin-loading">
        <Loader size={24} className="spinner" /> Chargement...
      </div>
    );

  if (error)
    return (
      <div className="admin-error">
        <XCircle size={24} /> {error}
      </div>
    );

  return (
    <div className="admin-page-container">
      <div className="admin-header-row">
        <h2>Utilisateurs en attente ({pendingUsers.length})</h2>

        <button onClick={fetchPendingUsers} className="btn-refresh">
          Rafraîchir
        </button>
      </div>

      <table className="pending-users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Nom complet</th>
            <th>Rôle</th>
            <th>Inscrit le</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {pendingUsers.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                Aucun utilisateur en attente 🎉
              </td>
            </tr>
          ) : (
            pendingUsers.map((user) => {
              const status = actionStatus[user.id];
              const disabled =
                status === "loading" ||
                status === "success" ||
                status === "deleted";

              return (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>
                    {user.first_name} {user.last_name}
                  </td>
                  <td>
                    <span className={`badge role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {new Date(user.created_at).toLocaleDateString("fr-FR")}
                  </td>

                  <td className="admin-actions">

                    {/* ✔ Validation */}
                    <button
                      disabled={disabled}
                      className="btn-validate"
                      onClick={() =>
                        handleValidateManual(user.id, user.email)
                      }
                    >
                      {status === "loading" && (
                        <Loader size={16} className="spinner" />
                      )}
                      {status === "success" && (
                        <CheckCircle size={16} color="green" />
                      )}

                      {status === "success"
                        ? " Validé"
                        : status === "loading"
                        ? " Validation..."
                        : "Valider"}
                    </button>

                    {/* ✉ Renvoyer email */}
                    <button
                      disabled={disabled}
                      className="btn-resend"
                      onClick={() =>
                        handleResendEmail(user.id, user.email)
                      }
                    >
                      {status === "loading" && (
                        <Loader size={16} className="spinner" />
                      )}
                      {status === "mail_sent" && (
                        <Mail size={16} color="blue" />
                      )}

                      {status === "mail_sent"
                        ? " Envoyé"
                        : status === "loading"
                        ? " Envoi..."
                        : "Email"}
                    </button>

                    {/* 🗑 Suppression */}
                    <button
                      disabled={disabled}
                      className="btn-delete"
                      onClick={() =>
                        handleDeletePendingUser(user.id, user.email)
                      }
                    >
                      {status === "deleted" ? "Supprimé" : <Trash2 size={16} />}
                    </button>

                    {/* ❌ Erreur */}
                    {status === "error" && (
                      <XCircle size={16} color="red" style={{ marginLeft: 8 }} />
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminPendingUsersPage;
